import { useEffect, useMemo, useState } from "react";
import { Globe, EyeOff, Eye, X, CheckCircle2, Circle, Search } from "lucide-react";
import { toast } from "sonner";
import { api, quarterOptions, quarterLabel, currentQuarter, formatApiError } from "../../lib/api";
import { ReportView } from "../../components/ReportView";
import { useTaxonomy } from "../../lib/useTaxonomy";

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37] w-full";

const SCALE = [
  { v: 1, label: "Foundation" }, { v: 2, label: "Emerging" }, { v: 3, label: "Age Appropriate" },
  { v: 4, label: "Strong for Age" }, { v: 5, label: "Elite for Age" },
];

const EvalForm = ({ player, quarter, meta, existing, onClose, onSaved }) => {
  const [scores, setScores] = useState(existing?.scores || {});
  const [isGk, setIsGk] = useState(existing?.is_goalkeeper || false);
  const [form, setForm] = useState({
    eval_date: existing?.eval_date || new Date().toISOString().slice(0, 10),
    coach: existing?.coach || "Blaine",
    doing_well: existing?.doing_well || "",
    next_quarter: existing?.next_quarter || "",
    parent_support: existing?.parent_support || "",
    coach_summary: existing?.coach_summary || "",
  });
  const [saving, setSaving] = useState(false);
  const scored = Object.keys(scores).length;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/admin/reports", { player_id: player.player_id, quarter, scores, is_goalkeeper: isGk, ...form });
      toast.success("Report saved as draft");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto" data-testid="eval-modal">
      <div className="max-w-4xl mx-auto my-8 bg-[#0A0A0A] border border-white/10 p-6 sm:p-10">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">{quarterLabel(quarter)} Evaluation</p>
            <h2 className="font-heading text-2xl font-black mt-1">{player.name} <span className="text-[#D4AF37] text-sm">{player.player_id}</span></h2>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="eval-close"><X size={22} /></button>
        </div>
        <p className="text-white/40 text-xs mb-8" data-testid="eval-scale-hint">Score 1-5: 1 Foundation · 2 Emerging · 3 Age Appropriate · 4 Strong for Age · 5 Elite for Age</p>
        {meta.metrics.map((pillar) => (
          <div key={pillar.pillar} className="mb-8">
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4 border-b border-white/10 pb-2">{pillar.pillar}</p>
            {pillar.groups.map((g) => {
              if (g.goalkeeping && !isGk) return null;
              return (
                <div key={g.name} className="mb-5">
                  <p className="text-[10px] uppercase tracking-widest text-white/40 mb-3">{g.name}</p>
                  <div className="space-y-2">
                    {g.metrics.map((m) => (
                      <div key={m.key} className="flex flex-wrap items-center justify-between gap-2 border border-white/5 bg-[#050505] px-4 py-3" data-testid={`eval-metric-${m.key}`}>
                        <span className="text-sm text-white/80">{m.label}</span>
                        <div className="flex gap-1.5">
                          {SCALE.map((s) => (
                            <button key={s.v} type="button" onClick={() => setScores({ ...scores, [m.key]: s.v })} title={s.label}
                              data-testid={`eval-score-${m.key}-${s.v}`}
                              className={`w-9 h-9 font-heading font-black text-sm rounded-sm transition-all duration-150 ${scores[m.key] === s.v ? "bg-[#D4AF37] text-black scale-110" : "border border-white/15 text-white/40 hover:border-[#D4AF37]/60"}`}>
                              {s.v}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <label className="flex items-center gap-3 text-sm text-white/70 mb-8">
          <input type="checkbox" checked={isGk} onChange={(e) => setIsGk(e.target.checked)} className="accent-[#D4AF37]" data-testid="eval-gk-toggle" />
          Goalkeeper — include goalkeeping metrics
        </label>
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div><label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Date of Evaluation</label>
            <input type="date" value={form.eval_date} onChange={(e) => setForm({ ...form, eval_date: e.target.value })} className={inputCls} data-testid="eval-date" /></div>
          <div><label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Coach Name</label>
            <input value={form.coach} onChange={(e) => setForm({ ...form, coach: e.target.value })} className={inputCls} data-testid="eval-coach" /></div>
          <div className="sm:col-span-2"><label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Doing well</label>
            <textarea rows={2} value={form.doing_well} onChange={(e) => setForm({ ...form, doing_well: e.target.value })} className={inputCls} data-testid="eval-doing-well" /></div>
          <div className="sm:col-span-2"><label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Next quarter focus</label>
            <textarea rows={2} value={form.next_quarter} onChange={(e) => setForm({ ...form, next_quarter: e.target.value })} className={inputCls} data-testid="eval-next-quarter" /></div>
          <div className="sm:col-span-2"><label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Parent support at home</label>
            <textarea rows={2} value={form.parent_support} onChange={(e) => setForm({ ...form, parent_support: e.target.value })} className={inputCls} data-testid="eval-parent-support" /></div>
          <div className="sm:col-span-2"><label className="block text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Coach Summary</label>
            <textarea rows={3} value={form.coach_summary} onChange={(e) => setForm({ ...form, coach_summary: e.target.value })} className={inputCls} data-testid="eval-summary" /></div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={submit} disabled={saving || scored === 0} className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-8 py-3.5 rounded-sm hover:bg-[#F3D059] disabled:opacity-50 gold-glow" data-testid="eval-save">
            {saving ? "Saving..." : `Save Draft (${scored} scored)`}
          </button>
          <p className="text-white/40 text-xs">Saved as draft — parents see nothing until you publish.</p>
        </div>
      </div>
    </div>
  );
};

export default function Reports() {
  const [quarter, setQuarter] = useState(currentQuarter());
  const [batch, setBatch] = useState("");
  const [centre, setCentre] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all / ready / pending / published
  const [search, setSearch] = useState("");
  const [batches, setBatches] = useState([]);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [evalFor, setEvalFor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { centreNames } = useTaxonomy();

  const load = () => api.get("/admin/reports", { params: { quarter, ...(batch ? { batch } : {}) } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [quarter, batch]);
  useEffect(() => { api.get("/admin/reports/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/staff/my-batches").then((r) => setBatches(r.data.batches)).catch(() => {}); }, []);

  const setPublished = async (playerId, published) => {
    try {
      await api.post("/admin/reports/publish", { player_id: playerId, quarter, published });
      toast.success(published ? "Report published" : "Report unpublished");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setReady = async (playerId, ready) => {
    try {
      await api.post("/admin/reports/mark-ready", { player_id: playerId, quarter, ready });
      toast.success(ready ? "Marked ready" : "Moved back to pending");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const publishAllReady = async () => {
    setBulkBusy(true);
    try {
      const { data: res } = await api.post(`/admin/reports/publish-all-ready`, null, { params: { quarter } });
      toast.success(`Published ${res.published} ready report${res.published === 1 ? "" : "s"}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBulkBusy(false);
  };

  const openPreview = async (playerId) => {
    try {
      const { data } = await api.get("/admin/reports/preview", { params: { player_id: playerId, quarter } });
      setPreview(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const players = data?.players || [];
  const reports = data?.reports || {};

  const filtered = useMemo(() => players.filter((p) => {
    if (centre && p.centre !== centre) return false;
    if (search && !`${p.player_id} ${p.name} ${p.batch || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    const r = reports[p.player_id];
    if (statusFilter === "ready" && !(r && r.ready && !r.published)) return false;
    if (statusFilter === "pending" && (r && (r.ready || r.published))) return false;
    if (statusFilter === "published" && !(r && r.published)) return false;
    return true;
  }), [players, centre, search, statusFilter, reports]);

  const readyPendingCount = players.filter((p) => reports[p.player_id]?.ready && !reports[p.player_id]?.published).length;

  return (
    <div data-testid="admin-reports">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Skill Reports</h1>
        <button onClick={publishAllReady} disabled={bulkBusy || readyPendingCount === 0}
          className="flex items-center gap-2 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-[#F3D059] transition-colors duration-200 disabled:opacity-40"
          data-testid="publish-all-ready">
          <Globe size={13} /> Publish All Ready ({readyPendingCount})
        </button>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] p-5 mb-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div><label className="block text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-2">Quarter</label>
            <select value={quarter} onChange={(e) => setQuarter(e.target.value)} className={inputCls} data-testid="reports-quarter">
              {quarterOptions(8).map((q) => <option key={q} value={q}>{quarterLabel(q)}</option>)}
            </select></div>
          <div><label className="block text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-2">Batch</label>
            <select value={batch} onChange={(e) => setBatch(e.target.value)} className={inputCls} data-testid="reports-batch">
              <option value="">All batches</option>
              {batches.map((b) => <option key={b} value={b}>{b}</option>)}
            </select></div>
          <div><label className="block text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-2">Centre</label>
            <select value={centre} onChange={(e) => setCentre(e.target.value)} className={inputCls} data-testid="reports-centre">
              <option value="">All centres</option>
              {centreNames.map((c) => <option key={c} value={c}>{c}</option>)}
            </select></div>
          <div><label className="block text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-2">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input placeholder="Player name or ID" value={search} onChange={(e) => setSearch(e.target.value)} className={inputCls + " pl-9"} data-testid="reports-search" />
            </div></div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap" data-testid="reports-status-filter">
          {["all", "pending", "ready", "published"].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors duration-200 ${statusFilter === s ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-[#D4AF37]"}`}
              data-testid={`reports-status-${s}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map((p) => {
          const report = reports[p.player_id];
          return (
            <div key={p.player_id} className="border border-white/10 bg-[#0A0A0A] p-4 sm:p-5" data-testid={`report-row-${p.player_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{p.name} <span className="text-[#D4AF37] text-xs ml-1">{p.player_id}</span></p>
                  <p className="text-white/40 text-xs mt-0.5">{p.batch || "No batch"}{p.centre ? ` · ${p.centre}` : ""}</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {report ? (
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${report.published ? "border-[#D4AF37]/40 text-[#D4AF37]" : report.ready ? "border-orange-400/40 text-orange-300" : "border-white/20 text-white/40"}`} data-testid={`report-status-${p.player_id}`}>
                      {report.published ? "Published" : report.ready ? "Ready" : `Draft · ${Object.keys(report.scores || {}).length} scored`}
                    </span>
                  ) : <span className="text-white/30 text-xs">No report</span>}
                  <button onClick={() => setEvalFor(p)} className="text-white/60 hover:text-white text-xs uppercase tracking-widest border border-white/20 px-3 py-2" data-testid={`report-edit-${p.player_id}`}>
                    {report ? "Edit Scores" : "Evaluate"}
                  </button>
                  {report && (
                    <button onClick={() => openPreview(p.player_id)} className="flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-widest border border-[#D4AF37]/50 px-3 py-2 hover:bg-[#D4AF37] hover:text-black" data-testid={`report-preview-${p.player_id}`}>
                      <Eye size={13} /> Preview
                    </button>
                  )}
                  {report && !report.published && (
                    <button onClick={() => setReady(p.player_id, !report.ready)}
                      className={`flex items-center gap-2 text-xs uppercase tracking-widest border px-3 py-2 transition-colors duration-200 ${report.ready ? "bg-orange-400/20 border-orange-400 text-orange-300" : "border-white/20 text-white/60 hover:border-orange-400 hover:text-orange-300"}`}
                      data-testid={`report-ready-${p.player_id}`}>
                      {report.ready ? <CheckCircle2 size={13} /> : <Circle size={13} />}
                      {report.ready ? "Ready" : "Mark as Ready"}
                    </button>
                  )}
                  {report && !report.published && (
                    <button onClick={() => setPublished(p.player_id, true)} className="flex items-center gap-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#F3D059]" data-testid={`report-publish-${p.player_id}`}>
                      <Globe size={13} /> Publish
                    </button>
                  )}
                  {report?.published && (
                    <button onClick={() => setPublished(p.player_id, false)} className="flex items-center gap-2 border border-white/20 text-white/60 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-sm hover:border-red-400 hover:text-red-400" data-testid={`report-unpublish-${p.player_id}`}>
                      <EyeOff size={13} /> Unpublish
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {data && filtered.length === 0 && <p className="text-white/40 text-sm border border-white/10 p-8 text-center">No players match the current filters.</p>}
      </div>

      {evalFor && meta && (
        <EvalForm player={evalFor} quarter={quarter} meta={meta} existing={reports[evalFor.player_id]} onClose={() => setEvalFor(null)} onSaved={load} />
      )}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={() => setPreview(null)} data-testid="preview-modal">
          <div className="max-w-4xl mx-auto my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 px-2">
              <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Parent Preview {preview.published ? "" : "· Draft — not visible to parents yet"}</p>
              <button onClick={() => setPreview(null)} className="text-white/50 hover:text-white" data-testid="preview-close"><X size={22} /></button>
            </div>
            <ReportView data={preview} testid="preview-report" />
          </div>
        </div>
      )}
    </div>
  );
}
